<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import LaisOperationalPage from 'dashboard/components-next/LaisOperationalPage.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisCardSkeleton from 'dashboard/components-next/LaisCardSkeleton.vue';
import LaisTabs from 'dashboard/components-next/LaisTabs.vue';
import LaisModal from 'dashboard/components-next/LaisModal.vue';
import LaisConfirmModal from 'dashboard/components-next/LaisConfirmModal.vue';
import SurveyMetaEditor from './components/SurveyMetaEditor.vue';
import SurveyQuestionsEditor from './components/SurveyQuestionsEditor.vue';
import SurveySuccessScreenEditor from './components/SurveySuccessScreenEditor.vue';
import SurveySettingsEditor from './components/SurveySettingsEditor.vue';
import SurveyPreview from './components/SurveyPreview.vue';
import { SURVEY_ROUTE_NAMES } from './constants';
import { findFormTemplate } from './formTemplates';
import { useSurveyFormEditor } from './composables/useSurveyFormEditor';
import { useSurveyLabels } from './composables/useSurveyLabels';
import {
  preventSurveyEditorUnload,
  shouldGuardSurveyEditorUnload,
} from './helper/surveyEditorUnloadHelper';

/**
 * Survey builder.
 *
 * Left column authors the questionnaire (identity, questions, success screen,
 * delivery rules); the right rail keeps a live preview of the public page. The
 * draft never leaves the composable, so leaving with unsaved work is caught by
 * the route guard.
 */
const route = useRoute();
const router = useRouter();
const { t, validationMessage } = useSurveyLabels();

const editor = useSurveyFormEditor();
const {
  form,
  questions,
  errors,
  errorCount,
  isDirty,
  isEditing,
  isLoading,
  isSaving,
  loadError,
  showErrors,
  resetForm,
  applyTemplate,
  updateForm,
  updateSuccessScreen,
  updateSettings,
  updateQuestion,
  setQuestions,
  addQuestion,
  removeQuestion,
  duplicateQuestion,
  moveQuestionUp,
  moveQuestionDown,
  setQuestionType,
  addOption,
  removeOption,
  updateOptionLabel,
  loadForm,
  save,
} = editor;

const activeTab = ref('questions');
const showPreviewModal = ref(false);
const pendingLeave = ref(null);
const loadFailed = ref(false);
let beforeUnloadGuardAttached = false;

const formId = computed(() => route.params.formId);

const pageTitle = computed(() =>
  isEditing.value
    ? t('SURVEYS.EDITOR.TITLE_EDIT')
    : t('SURVEYS.EDITOR.TITLE_NEW')
);

const pageError = computed(() =>
  loadFailed.value ? loadError.value || t('SURVEYS.EDITOR.LOAD_ERROR') : false
);

const responsesHref = computed(() =>
  form.value.id
    ? router.resolve({
        name: SURVEY_ROUTE_NAMES.responses,
        params: { accountId: route.params.accountId, formId: form.value.id },
      }).href
    : ''
);

const fetchForm = async () => {
  if (!formId.value) return;
  const result = await loadForm(formId.value);
  loadFailed.value = !result.ok;
};

const tabs = computed(() => [
  {
    id: 'questions',
    label: t('SURVEYS.EDITOR.TABS.QUESTIONS'),
    icon: 'i-lucide-list-checks',
    badge: questions.value.length,
  },
  {
    id: 'success',
    label: t('SURVEYS.EDITOR.TABS.SUCCESS'),
    icon: 'i-lucide-party-popper',
  },
  {
    id: 'settings',
    label: t('SURVEYS.EDITOR.TABS.SETTINGS'),
    icon: 'i-lucide-settings-2',
  },
]);

const questionsListError = computed(() =>
  showErrors.value ? validationMessage(errors.value.questions) : ''
);

const metaErrors = computed(() =>
  showErrors.value ? { name: errors.value.name, kind: errors.value.kind } : {}
);

const successErrors = computed(() =>
  showErrors.value ? errors.value.success_screen : {}
);

const settingsErrors = computed(() =>
  showErrors.value ? errors.value.settings : {}
);

const validationSummary = computed(() =>
  showErrors.value && errorCount.value
    ? t('SURVEYS.EDITOR.VALIDATION_SUMMARY', { count: errorCount.value })
    : ''
);

const goToList = () => {
  router.push({ name: SURVEY_ROUTE_NAMES.index });
};

const onSave = async () => {
  const result = await save();

  if (result.invalid) {
    useAlert(t('SURVEYS.EDITOR.ALERTS.INVALID'));
    return;
  }

  if (!result.ok) {
    useAlert(result.message || t('SURVEYS.EDITOR.ALERTS.SAVE_ERROR'));
    return;
  }

  useAlert(t('SURVEYS.EDITOR.ALERTS.SAVED'));
  goToList();
};

const confirmLeave = () => {
  const next = pendingLeave.value;
  pendingLeave.value = null;
  if (next) next();
};

const cancelLeave = () => {
  const next = pendingLeave.value;
  pendingLeave.value = null;
  // Abort the pending navigation explicitly, otherwise the router keeps it
  // half-resolved until another navigation cancels it.
  if (next) next(false);
};

const handleBeforeUnload = event => preventSurveyEditorUnload(event);

const removeBeforeUnloadGuard = () => {
  if (!beforeUnloadGuardAttached || typeof window === 'undefined') return;

  window.removeEventListener('beforeunload', handleBeforeUnload);
  beforeUnloadGuardAttached = false;
};

const syncBeforeUnloadGuard = () => {
  if (typeof window === 'undefined') return;

  const shouldGuard = shouldGuardSurveyEditorUnload({
    isDirty: isDirty.value,
    isSaving: isSaving.value,
  });
  if (!shouldGuard) {
    removeBeforeUnloadGuard();
    return;
  }
  if (beforeUnloadGuardAttached) return;

  window.addEventListener('beforeunload', handleBeforeUnload);
  beforeUnloadGuardAttached = true;
};

onBeforeRouteLeave((to, from, next) => {
  if (!isDirty.value || isSaving.value) {
    next();
    return;
  }
  pendingLeave.value = next;
});

watch([isDirty, isSaving], syncBeforeUnloadGuard, { immediate: true });
onBeforeUnmount(removeBeforeUnloadGuard);

onMounted(async () => {
  if (formId.value) {
    await fetchForm();
    return;
  }
  const templateDefinition = findFormTemplate(route.query.template);
  if (templateDefinition) {
    applyTemplate(templateDefinition);
    return;
  }
  resetForm();
});
</script>

<template>
  <LaisOperationalPage
    :title="pageTitle"
    :helper="t('SURVEYS.EDITOR.HELPER')"
    :error="pageError"
    show-back
    :aside-label="t('SURVEYS.PREVIEW.TITLE')"
    @back="goToList"
    @retry="fetchForm"
  >
    <template #actions>
      <LaisPrimaryButton
        variant="ghost"
        icon="i-lucide-eye"
        class="lg:hidden"
        @click="showPreviewModal = true"
      >
        {{ t('SURVEYS.EDITOR.PREVIEW_BUTTON') }}
      </LaisPrimaryButton>
      <LaisPrimaryButton
        v-if="isEditing"
        variant="ghost"
        icon="i-lucide-bar-chart-3"
        tag="a"
        :href="responsesHref"
      >
        {{ t('SURVEYS.EDITOR.RESPONSES_BUTTON') }}
      </LaisPrimaryButton>
    </template>

    <LaisCardSkeleton v-if="isLoading" :count="2" />

    <div v-else class="flex min-w-0 flex-col gap-4">
      <LaisCard :title="t('SURVEYS.EDITOR.META.TITLE')" density="tight">
        <SurveyMetaEditor
          :form="form"
          :errors="metaErrors"
          @update="updateForm"
        />
      </LaisCard>

      <LaisTabs
        v-model="activeTab"
        :tabs="tabs"
        :aria-label="t('SURVEYS.EDITOR.TABS.ARIA_LABEL')"
      >
        <template #questions>
          <SurveyQuestionsEditor
            :questions="questions"
            :question-errors="showErrors ? errors.question_errors : {}"
            :list-error="questionsListError"
            @reorder="setQuestions"
            @add="addQuestion"
            @update="updateQuestion"
            @change-type="setQuestionType"
            @remove="removeQuestion"
            @duplicate="duplicateQuestion"
            @move-up="moveQuestionUp"
            @move-down="moveQuestionDown"
            @add-option="addOption"
            @remove-option="removeOption"
            @update-option="updateOptionLabel"
          />
        </template>

        <template #success>
          <LaisCard density="tight">
            <SurveySuccessScreenEditor
              :success-screen="form.success_screen"
              :errors="successErrors"
              @update="updateSuccessScreen"
            />
          </LaisCard>
        </template>

        <template #settings>
          <LaisCard density="tight">
            <SurveySettingsEditor
              :settings="form.settings"
              :errors="settingsErrors"
              @update="updateSettings"
            />
          </LaisCard>
        </template>
      </LaisTabs>
    </div>

    <template #aside>
      <div class="hidden min-w-0 lg:block">
        <h2
          class="mb-2 font-redHatDisplay text-lais-heading-sm font-semibold text-lais-text"
        >
          {{ t('SURVEYS.PREVIEW.TITLE') }}
        </h2>
        <p class="mb-3 text-lais-body-sm text-lais-text-muted">
          {{ t('SURVEYS.PREVIEW.SUBTITLE') }}
        </p>
        <SurveyPreview :form="form" />
      </div>
    </template>

    <template #footer>
      <div class="flex flex-wrap items-center justify-between gap-3">
        <p
          v-if="validationSummary"
          class="flex items-center gap-2 text-[13px] font-semibold text-lais-danger"
          role="alert"
        >
          <span class="i-lucide-triangle-alert size-4" aria-hidden="true" />
          {{ validationSummary }}
        </p>
        <p
          v-else-if="isDirty"
          class="flex items-center gap-2 text-[13px] text-lais-text-muted"
        >
          <span class="i-lucide-circle-dot size-3.5" aria-hidden="true" />
          {{ t('SURVEYS.EDITOR.UNSAVED') }}
        </p>
        <span v-else />

        <div class="flex flex-wrap items-center gap-2">
          <LaisPrimaryButton variant="ghost" @click="goToList">
            {{ t('SURVEYS.EDITOR.CANCEL') }}
          </LaisPrimaryButton>
          <LaisPrimaryButton
            icon="i-lucide-check"
            :is-loading="isSaving"
            @click="onSave"
          >
            {{ t('SURVEYS.EDITOR.SAVE') }}
          </LaisPrimaryButton>
        </div>
      </div>
    </template>
  </LaisOperationalPage>

  <LaisModal
    :show="showPreviewModal"
    :title="t('SURVEYS.PREVIEW.TITLE')"
    full-screen
    @close="showPreviewModal = false"
  >
    <SurveyPreview :form="form" />
  </LaisModal>

  <LaisConfirmModal
    :show="Boolean(pendingLeave)"
    :title="t('SURVEYS.EDITOR.LEAVE.TITLE')"
    :description="t('SURVEYS.EDITOR.LEAVE.DESCRIPTION')"
    :confirm-text="t('SURVEYS.EDITOR.LEAVE.CONFIRM')"
    :cancel-text="t('SURVEYS.EDITOR.LEAVE.CANCEL')"
    icon="i-lucide-triangle-alert"
    @close="cancelLeave"
    @confirm="confirmLeave"
  />
</template>
